/* tslint:disable */
import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';
import { createEmailList, deleteEmailList, getEmailLists, updateEmailList } from 'src/helpers/api';
import { pluralizeWithCount } from 'src/utils/stringUtils';
import { EmailList, EmailListItem } from '../../types/EmailLists';
import Button from '../Button';
import Input from '../FormElements/Input';
import Label from '../FormElements/Label';
import Icon from '../Icon';
import { DotLoader } from '../Loaders';

type EmailListItemProps = {
  list: EmailList;
  isActive: boolean;
  onToggleEdit: (list: EmailList | null) => void;
  onDelete: (list: EmailList) => void;
  onSave: (list: EmailList) => Promise<void>;
}

const EmailListItem = ({
  list,
  isActive,
  onToggleEdit,
  onDelete,
  onSave,
}: EmailListItemProps) => {
  const emailListRef = React.useRef<HTMLDivElement>(null);
  const nameRef = React.useRef<HTMLInputElement>(null);
  const [name, setName] = React.useState<string>(() => {
    return list.name || '';
  });
  const [emailsAddresses, setEmailAddresses] = React.useState<string[]>(() => {
    return list.coursePortalEmails.map((item) => item.emailAddress);
  });

  const addEmailAddress = () => {
    setEmailAddresses(emailsAddresses.concat(['']));
  };

  const removeEmailAddress = (indexToRemove: number) => {
    const nextEmailAddresses = emailsAddresses.filter((_, index) => index !== indexToRemove);
    setEmailAddresses(nextEmailAddresses);
  };

  const handleNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (target) {
      setName(target.value);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    let name = '';
    const emails = [];

    if (nameRef.current) {
      name = nameRef.current.value;
    }

    if (emailListRef.current) {
      const inputs = emailListRef.current.querySelectorAll('input[type=email]');
      for (let index = 0; index < inputs.length; index += 1) {
        const input = inputs[index] as HTMLInputElement;
        const email = input.value as string;
        emails.push(email);
      }
    }

    const coursePortalEmails: EmailListItem[] = emails.map((emailAddress) => ({ emailAddress }))

    onSave({
      id: list.id,
      isNew: list.isNew,
      name,
      coursePortalEmails,
    });

    event.preventDefault();
  };

  React.useEffect(() => {
    if (nameRef.current) {
      nameRef.current.value = list.name;
    }
  }, [list.name]);

  const handleToggleEdit = (list: EmailList) => {
    onToggleEdit(isActive ? null : list);
  };

  React.useLayoutEffect(() => {
    if (emailListRef.current && isActive) {
      const items = emailListRef.current.querySelectorAll('input');
      const item = items[items.length - 1];
      if (item) {
        item.focus();
      }
    }
  }, [emailsAddresses, isActive]);

  return (
    <div className={classNames(
      'border-b border-gray-body transition-bg-color',
      { 'has-background-gray-500': isActive },
    )}>
      <div
        onClick={() => handleToggleEdit(list)}
        className="p-4 px-5 cursor-pointer flex items-center justify-between duration-100 transition-bg-color hover:bg-gray-500"
      >
        <div>
          <div className="b">{name || <span className="italic opacity-75">Ny emailgruppe</span>}</div>
          <div className="opacity-75">
            {emailsAddresses.length
              ? `${pluralizeWithCount(emailsAddresses.length, 'e-mailadresse', 'e-mailadresser')} i gruppen`
              : 'Ingen e-mailadresser i gruppen endnu'}
          </div>
        </div>
        <div className="flex items-center">
          <span className={classNames('f5 font-semibold transition-opacity', isActive ? 'opacity-0' : 'opacity-60')}>Rediger gruppen</span>
          <Icon
            name="chevron-down"
            className={classNames(
              'ml-2 transition-transform',
              { 'rotate-180': isActive },
            )}
          />
        </div>
      </div>
      <AnimateHeight height={isActive ? 'auto' : 0} duration={450}>
        <form onSubmit={handleSubmit} className="p-5 pt0">
          <div>
            <Label htmlFor="name">Navn</Label>
            <Input
              type="text"
              className="b"
              name="name"
              id="name"
              required={true}
              minLength={1}
              maxLength={60}
              defaultValue={list.name}
              elementSize="large"
              autoComplete="off"
              onChange={handleNameChange}
              ref={nameRef}
            />
          </div>
          <div className="mt-4" ref={emailListRef}>
            <Label>E-mailadresser</Label>
            {emailsAddresses.length === 0 && (
              <div className="text-center mt-2 p-8 b border border-dark-gray-body">
                <div>Ingen e-mailadresser tilknyttet endnu</div>

                <Button
                  className="mx-auto block mt-2 cursor-pointer"
                  type="button"
                  color="primary" onClick={addEmailAddress}
                >
                  <Icon name="plus" className="mr-2" />
                  Tilføj e-mailadresse til gruppen
                </Button>
              </div>
            )}
            {emailsAddresses.length > 0 && (
              <div>
                {emailsAddresses.map((emailAddress, index) =>
                  <div key={index} className="mb-2 flex items-center">
                    <Input
                      type="email"
                      defaultValue={emailAddress}
                      placeholder="Udfyld e-mailadresse"
                      name={`email${index + 1}`}
                      required={true}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="h-full flex items-center justify-end w-8 opacity-60 cursor-pointer"
                      onClick={() => removeEmailAddress(index)}
                    >
                      <Icon name="cross" />
                    </button>
                  </div>
                )}

                <Button type="button" color="primary" onClick={addEmailAddress}>
                  <Icon name="plus" className="mr-2" />
                  Tilføj e-mailadresse
                </Button>
              </div>
            )}
          </div>

          <div className="mt-5 flex items-center border-t border-dark-gray-body pt-5">
            <Button
              type="button"
              color="danger"
              className="mr-auto"
              onClick={() => onDelete(list)}
            >
              Slet gruppe
            </Button>
            <Button
              type="button"
              color="light"
              onClick={() => {
                setName(list.name || '');
                setEmailAddresses(list.coursePortalEmails.map((item) => item.emailAddress));
                onToggleEdit(null)
              }}
            >
              Annuller
            </Button>
            <Button
              type="submit"
              color="primary"
              className="ml-2"
            >
              Gem ændringer
            </Button>
          </div>
        </form>
      </AnimateHeight>
    </div>
  );
};

export type EmailListsProps = {
  isOpen: boolean;
  onClose: () => void;
};

const EmailLists = (props: EmailListsProps) => {
  const [activeListId, setActiveListId] = React.useState<string | null>();
  const [items, setItems] = React.useState<EmailList[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (props.isOpen) {
      setIsLoading(true);
      getEmailLists().then((nextItems) => {
        setItems(nextItems);
        setIsLoading(false);
      });
    }
  }, [props.isOpen]);

  const handleDelete = ({ id, isNew }: EmailList) => {
    const nextItems = items.filter((list) => list.id !== id);

    setItems(nextItems);
    if (activeListId === id) {
      setActiveListId(null);
    }

    if (isNew !== true) {
      deleteEmailList(id);
    }
  };

  const handleSave = (list: EmailList) => {
    const onDone = (attributes: EmailList) => {
      setActiveListId(null);

      const nextItems = items.map((listItem) => {
        console.log(list.id, listItem);
        if (listItem.id === list.id) {
          return attributes;
        }

        return listItem;
      });
      setItems(nextItems);
    };

    if (list.isNew) {
      return createEmailList(list)
        .then(onDone);
    }

    return updateEmailList(list)
      .then(onDone);
  };

  const handleAddNew = () => {
    const id = `_newItem${Math.random() * 1000}`;
    const newItem: EmailList = {
      id,
      isNew: true,
      name: '',
      coursePortalEmails: []
    };
    setItems(items.concat([newItem]))
    setActiveListId(id);
  };

  if (!props.isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex flex-column items-center justify-center z-20 has-background-gray-body p-6">
      <div className="w-full max-w-2xl flex justify-end pb-1">
        <button
          onClick={props.onClose}
          className="flex items-center p-2 f6 font-bold uppercase cursor-pointer"
        >
          Luk
          <Icon name="cross" className="-mr-2" />
        </button>
      </div>
      <div className="has-background-white flex-none w-full max-w-2xl p-12">
        <div className="f2 mb-2">Emailgrupper</div>
        <p>Her kan du oprette nye emailgrupper og redigere eksisterende.</p>
        {isLoading && items.length === 0 && (
          <div className="p-8 text-center">
            <DotLoader inline={true} />
          </div>
        )}
        {isLoading === false && items.length === 0 && (
          <div className="text-center p-8 b border border-dark-gray-body">
            <div>Ingen emailgrupper oprettet endnu</div>

            <Button
              className="mx-auto block mt-4 cursor-pointer"
              type="button"
              color="primary" onClick={handleAddNew}
            >
              <Icon name="plus" className="mr-2" />
              Opret ny emailgruppe
            </Button>
          </div>
        )}
        {items.map((list) =>
          <EmailListItem
            key={list.id}
            list={list}
            isActive={activeListId === list.id}
            onToggleEdit={(toggle) => toggle ? setActiveListId(toggle.id) : setActiveListId(null)}
            onDelete={handleDelete}
            onSave={handleSave}
          />
        )}
        <div>
          <Button
            className="mx-auto block mt-2 cursor-pointer"
            type="button"
            color="primary" onClick={handleAddNew}
          >
            <Icon name="plus" className="mr-2" />
            Tilføj emailgruppe
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EmailLists;



// WEBPACK FOOTER //
// ./src/components/EmailLists/index.tsx